import { http } from "@/lib/http";

import type {
  Stack,
  StackActionRequest,
  StackCreateRequest,
  StackDrift,
  StackEvent,
  StackOutput,
  StackPreview,
  StackResource,
  StackTemplateResponse,
  StackUpdatePreview,
  StackUpdateRequest,
  TemplateValidationResult,
} from "./types";

export async function listStacks(): Promise<Stack[]> {
  const { data } = await http.get<Stack[]>("/orchestration/stacks");
  return data;
}

export async function getStack(stackId: string): Promise<Stack> {
  const { data } = await http.get<Stack>(`/orchestration/stacks/${stackId}`);
  return data;
}

export async function getStackTemplate(
  stackId: string,
): Promise<StackTemplateResponse> {
  const { data } = await http.get<StackTemplateResponse>(
    `/orchestration/stacks/${stackId}/template`,
  );
  return data;
}

export async function listStackResources(
  stackId: string,
): Promise<StackResource[]> {
  const { data } = await http.get<StackResource[]>(
    `/orchestration/stacks/${stackId}/resources`,
  );
  return data;
}

export async function listStackEvents(
  stackId: string,
): Promise<StackEvent[]> {
  const { data } = await http.get<StackEvent[]>(
    `/orchestration/stacks/${stackId}/events`,
  );
  return data;
}

export async function validateTemplate(
  template: string,
): Promise<TemplateValidationResult> {
  const { data } = await http.post<TemplateValidationResult>(
    "/orchestration/templates/validate",
    { template },
  );
  return data;
}

/** What a create would build. Nothing is created. */
export async function previewStack(
  payload: StackCreateRequest,
): Promise<StackPreview> {
  const { data } = await http.post<StackPreview>(
    "/orchestration/stacks/preview",
    payload,
  );
  return data;
}

/** What an update would do to each resource. The blueprint is not touched. */
export async function previewStackUpdate(
  stackId: string,
  payload: StackUpdateRequest,
): Promise<StackUpdatePreview> {
  const { data } = await http.post<StackUpdatePreview>(
    `/orchestration/stacks/${stackId}/preview`,
    payload,
  );
  return data;
}

export async function createStack(
  payload: StackCreateRequest,
): Promise<Stack> {
  const { data } = await http.post<Stack>("/orchestration/stacks", payload);
  return data;
}

export async function updateStack(
  stackId: string,
  payload: StackUpdateRequest,
): Promise<Stack> {
  const { data } = await http.put<Stack>(
    `/orchestration/stacks/${stackId}`,
    payload,
  );
  return data;
}

export async function deleteStack(stackId: string): Promise<void> {
  await http.delete(`/orchestration/stacks/${stackId}`);
}

export async function getStackOutputs(
  stackId: string,
): Promise<StackOutput[]> {
  const { data } = await http.get<StackOutput[]>(
    `/orchestration/stacks/${stackId}/outputs`,
  );
  return data;
}

/** Every resource compared with the template now (nothing is stored). */
export async function getStackDrift(stackId: string): Promise<StackDrift> {
  const { data } = await http.get<StackDrift>(`/orchestration/stacks/${stackId}/drift`);
  return data;
}

export async function runStackAction(
  stackId: string,
  payload: StackActionRequest,
): Promise<void> {
  await http.post(`/orchestration/stacks/${stackId}/actions`, payload);
}
